import dayjs from 'dayjs';
import { getSyncPresetById, SYNC_PROTOCOL_PRESETS } from '../constants/syncProtocolPresets';

export function mapApiTemplateToPreset(template) {
  if (!template) return null;
  const steps = template.steps || [];
  const headNote = template.target_head_count
    ? `Planned batch: ~${template.target_head_count} head`
    : null;

  return {
    id: `custom-${template.id}`,
    templateId: template.id,
    name: template.name,
    species: template.species || [],
    stepCount: steps.length,
    summary: template.summary || headNote || 'Custom synchronization protocol',
    sourceNote: template.source_note || 'Custom template saved by your farm team.',
    steps,
    isCustom: true,
    targetHeadCount: template.target_head_count,
  };
}

export function resolveSyncPreset(presetId, customPresets = []) {
  if (!presetId) return null;
  const builtIn = getSyncPresetById(presetId);
  if (builtIn) return builtIn;
  return customPresets.find((preset) => preset.id === presetId) || null;
}

export function getSyncPresetsForSpeciesWithCustom(speciesKey, customPresets = []) {
  const key = speciesKey ? speciesKey.toLowerCase() : null;
  const builtIn = key
    ? SYNC_PROTOCOL_PRESETS.filter((preset) => preset.species.includes(key))
    : SYNC_PROTOCOL_PRESETS;
  const custom = key
    ? customPresets.filter((preset) => (preset.species || []).includes(key))
    : customPresets;

  return [...builtIn, ...custom];
}

const EMPTY_STATE = {
  presetId: null,
  presetName: null,
  programName: null,
  syncGroupId: null,
  sync_group_id: null,
  day0Date: null,
  endDate: null,
  inseminationDate: null,
  inseminationTime: null,
  inseminationNotes: null,
  steps: [],
};

export function createEmptySyncProtocolState() {
  return { ...EMPTY_STATE, steps: [] };
}

function finalizeSyncState(state) {
  const steps = state.steps || [];
  const first = steps[0];
  const last = steps[steps.length - 1];
  const aiStep = steps.find((s) => s.action === 'insemination') || last;

  return {
    ...state,
    day0Date: first?.scheduledDate || state.day0Date,
    endDate: last?.scheduledDate || null,
    inseminationDate: aiStep?.scheduledDate || null,
    inseminationTime: aiStep?.actualTime || aiStep?.scheduledTime || state.inseminationTime,
    inseminationNotes: aiStep?.notes ?? state.inseminationNotes,
  };
}

export function buildScheduleFromPreset(preset, anchorDate, previousSteps = [], anchorTime) {
  if (!preset || !anchorDate) {
    return createEmptySyncProtocolState();
  }

  const anchor = dayjs(anchorDate).startOf('day');
  const prevByKey = Object.fromEntries((previousSteps || []).map((s) => [s.key, s]));
  const firstTemplate = preset.steps[0];
  const firstTime = anchorTime || prevByKey[firstTemplate.key]?.scheduledTime || firstTemplate.defaultTime || '08:00';

  const steps = preset.steps.map((template, index) => {
    const scheduled = anchor.add(template.dayOffset - firstTemplate.dayOffset, 'day');
    const prev = prevByKey[template.key];
    const time = index === 0
      ? firstTime
      : (prev?.scheduledTime || template.defaultTime || '08:00');

    return {
      key: template.key,
      dayOffset: template.dayOffset,
      title: template.title,
      action: template.action,
      hormone: template.hormone,
      productExamples: template.productExamples,
      dosage: template.dosage,
      route: template.route,
      instructions: template.instructions,
      inventory_item_id: template.inventory_item_id || prev?.inventory_item_id || null,
      inventory_item_name: template.inventory_item_name || prev?.inventory_item_name || null,
      inventory_deducted: prev?.inventory_deducted || false,
      inventory_deducted_qty: prev?.inventory_deducted_qty || null,
      defaultTime: template.defaultTime || '08:00',
      scheduledDate: scheduled.format('YYYY-MM-DD'),
      scheduledTime: time,
      actualDate: prev?.actualDate || null,
      actualTime: prev?.actualTime || null,
      actualDosage: parseDosageMl(prev?.actualDosage) ?? parseDosageMl(template.dosage),
      status: prev?.status || 'pending',
      notes: prev?.notes || '',
    };
  });

  return finalizeSyncState({
    presetId: preset.id,
    presetName: preset.name,
    programName: preset.name,
    day0Date: anchor.format('YYYY-MM-DD'),
    steps,
  });
}

function timeToMinutes(timeStr) {
  const [h, m] = (timeStr || '08:00').split(':').map(Number);
  return h * 60 + m;
}

/** Shift a step's date and time together, so a time pushed past midnight lands on the next day */
function shiftDateTimeByMinutes(dateStr, timeStr, deltaMinutes) {
  const shifted = dayjs(dateStr).startOf('day').add(timeToMinutes(timeStr) + deltaMinutes, 'minute');
  return { date: shifted.format('YYYY-MM-DD'), time: shifted.format('HH:mm') };
}

/** Shift all step dates/times when the first step date/time changes */
export function recalculateFromFirstStep(state, firstDate, firstTime, customPresets = []) {
  const steps = state?.steps || [];
  if (!steps.length || !firstDate) return state;

  const preset = resolveSyncPreset(state.presetId, customPresets);
  const templatesByKey = preset
    ? Object.fromEntries(preset.steps.map((t) => [t.key, t]))
    : {};

  const first = steps[0];
  const firstTemplate = templatesByKey[first.key] || preset?.steps?.[0];
  const anchorOffset = first.dayOffset ?? 0;
  const timeStr = firstTime || first.scheduledTime || '08:00';
  const presetFirstTime = first.defaultTime || firstTemplate?.defaultTime || '08:00';
  const timeDelta = timeToMinutes(timeStr) - timeToMinutes(presetFirstTime);

  const updatedSteps = steps.map((step, index) => {
    const daysDiff = (step.dayOffset ?? 0) - anchorOffset;
    const newDate = dayjs(firstDate).add(daysDiff, 'day').format('YYYY-MM-DD');
    const template = templatesByKey[step.key];
    const presetStepTime = step.defaultTime || template?.defaultTime || step.scheduledTime || '08:00';

    if (index === 0) {
      return {
        ...step,
        scheduledDate: dayjs(firstDate).format('YYYY-MM-DD'),
        scheduledTime: timeStr,
      };
    }

    const shifted = shiftDateTimeByMinutes(newDate, presetStepTime, timeDelta);
    return {
      ...step,
      scheduledDate: shifted.date,
      scheduledTime: shifted.time,
    };
  });

  return finalizeSyncState({
    ...state,
    steps: updatedSteps,
  });
}

export function parseSyncProtocolData(raw) {
  if (!raw) return createEmptySyncProtocolState();
  if (typeof raw === 'string') {
    try {
      return parseSyncProtocolData(JSON.parse(raw));
    } catch {
      return createEmptySyncProtocolState();
    }
  }
  if (typeof raw !== 'object') return createEmptySyncProtocolState();
  return finalizeSyncState({
    ...createEmptySyncProtocolState(),
    ...raw,
    steps: Array.isArray(raw.steps) ? raw.steps : [],
  });
}

export function serializeSyncProtocolData(state) {
  const parsed = parseSyncProtocolData(state);
  if (!parsed.presetId || !parsed.steps?.length) return null;
  const syncGroupId = parsed.syncGroupId || parsed.sync_group_id || null;
  return {
    presetId: parsed.presetId,
    presetName: parsed.presetName,
    programName: parsed.programName || parsed.presetName || null,
    syncGroupId,
    sync_group_id: syncGroupId,
    day0Date: parsed.day0Date,
    endDate: parsed.endDate,
    inseminationDate: parsed.inseminationDate,
    inseminationTime: parsed.inseminationTime,
    inseminationNotes: parsed.inseminationNotes,
    steps: parsed.steps,
  };
}

/** Force all steps pending when creating a new sync program (schedule-only, no field execution yet). */
export function resetSyncStepsForScheduling(state) {
  const parsed = parseSyncProtocolData(state);
  if (!parsed?.steps?.length) return state;
  return finalizeSyncState({
    ...parsed,
    steps: parsed.steps.map((step) => ({
      ...step,
      status: 'pending',
      actualDate: null,
      actualTime: null,
      inventory_deducted: false,
      inventory_deducted_qty: null,
    })),
  });
}

export function applySyncStateToBreedingFields(state) {
  const parsed = parseSyncProtocolData(state);
  if (!parsed.presetId) return {};

  const aiStep = parsed.steps.find((s) => s.action === 'insemination');
  const insemDate = aiStep?.actualDate || parsed.inseminationDate;
  const insemTime = aiStep?.actualTime || parsed.inseminationTime;

  return {
    sync_protocol_name: (parsed.programName || '').trim() || parsed.presetName,
    sync_protocol_start_date: parsed.day0Date,
    sync_protocol_end_date: parsed.endDate,
    insemination_schedule_date: insemDate,
    sync_protocol_data: serializeSyncProtocolData(parsed),
  };
}

export function rebuildSyncStateFromPresetChange(presetId, anchorDate, previousState, customPresets = []) {
  const preset = resolveSyncPreset(presetId, customPresets);
  if (!preset) return createEmptySyncProtocolState();
  const prevSteps = previousState?.presetId === presetId ? previousState.steps : [];
  const date = anchorDate || previousState?.day0Date || dayjs().format('YYYY-MM-DD');
  const firstTime = prevSteps[0]?.scheduledTime;
  const next = buildScheduleFromPreset(preset, date, prevSteps, firstTime);
  const previousName = (previousState?.programName || '').trim();
  const previousPreset = (previousState?.presetName || '').trim();
  const keepCustomName = previousName && previousName !== previousPreset;
  const syncGroupId = previousState?.syncGroupId || previousState?.sync_group_id || null;
  return {
    ...next,
    programName: keepCustomName ? previousName : preset.name,
    syncGroupId,
    sync_group_id: syncGroupId,
  };
}

export function updateSyncStep(state, stepKey, patch, customPresets = []) {
  const stepIndex = (state?.steps || []).findIndex((s) => s.key === stepKey);
  const isFirstStep = stepIndex === 0;

  if (!isFirstStep && (patch.scheduledDate !== undefined || patch.scheduledTime !== undefined)) {
    const { scheduledDate, scheduledTime, ...allowedPatch } = patch;
    if (!Object.keys(allowedPatch).length) return state;
    patch = allowedPatch;
  }

  if (isFirstStep && (patch.scheduledDate !== undefined || patch.scheduledTime !== undefined)) {
    const first = state.steps[0];
    const newDate = patch.scheduledDate ?? first.scheduledDate;
    const newTime = patch.scheduledTime ?? first.scheduledTime;
    const recalculated = recalculateFromFirstStep(state, newDate, newTime, customPresets);
    const mergedFirst = {
      ...recalculated.steps[0],
      ...patch,
      scheduledDate: newDate,
      scheduledTime: newTime,
    };
    return finalizeSyncState({
      ...recalculated,
      steps: recalculated.steps.map((s, i) => (i === 0 ? mergedFirst : s)),
    });
  }

  const steps = (state?.steps || []).map((step) => (
    step.key === stepKey ? { ...step, ...patch } : step
  ));

  return finalizeSyncState({
    ...state,
    steps,
  });
}

export function countCompletedSteps(state) {
  return (state?.steps || []).filter((s) => s.status === 'done').length;
}

export function getSyncCompletionPercent(syncProtocolData) {
  const parsed = parseSyncProtocolData(syncProtocolData);
  const total = parsed.steps?.length || 0;
  if (!total) return null;
  const done = countCompletedSteps(parsed);
  return Math.round((done / total) * 100);
}

function escapeCsvCell(value) {
  const stringValue = String(value ?? '');
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
}

function getAnimalLabelFromBreeding(record) {
  const animal = record?.animal;
  return animal?.name || animal?.tag_number || `#${record?.animal_id || animal?.id || '?'}`;
}

export function mapSyncBreedingExportRow(record) {
  const parsed = parseSyncProtocolData(record.sync_protocol_data);
  const total = parsed.steps?.length || 0;
  const done = countCompletedSteps(parsed);
  const pct = total ? Math.round((done / total) * 100) : null;
  const aiDate = record.insemination_schedule_date || parsed.inseminationDate;

  return {
    animal: getAnimalLabelFromBreeding(record),
    protocol: record.sync_protocol_name || parsed.presetName || '',
    start: record.sync_protocol_start_date || parsed.day0Date || '',
    aiDate: aiDate || '',
    stepsDone: total ? `${done}/${total}` : '',
    completionPct: pct != null ? `${pct}%` : '',
    status: record.status || '',
    breedingDate: record.breeding_date || '',
  };
}

export function exportSyncBreedingsCsv(breedings) {
  const headers = ['Animal', 'Protocol', 'Start', 'AI date', 'Steps', 'Completion %', 'Status', 'Breeding date'];
  const rows = breedings.map(mapSyncBreedingExportRow);

  const csv = [
    headers.map(escapeCsvCell).join(','),
    ...rows.map((row) => [
      row.animal,
      row.protocol,
      row.start,
      row.aiDate,
      row.stepsDone,
      row.completionPct,
      row.status,
      row.breedingDate,
    ].map(escapeCsvCell).join(',')),
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  const dateStr = new Date().toISOString().slice(0, 10);
  link.href = url;
  link.download = `estrus_sync_${dateStr}.csv`;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Extract numeric ml from preset text like "2 ml" or a stored number */
export function parseDosageMl(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  const str = String(value);
  const mlMatch = str.match(/(\d+(?:\.\d+)?)\s*ml/i);
  if (mlMatch) return Number(mlMatch[1]);
  const num = Number(str);
  return Number.isNaN(num) ? null : num;
}

export function formatDosageMl(value) {
  const ml = parseDosageMl(value);
  return ml != null ? `${ml} ml` : null;
}

export function formatSyncStepActionLine(step) {
  const dose = formatDosageMl(step.actualDosage) || step.dosage;
  const route = step.route ? ` via ${step.route}` : '';

  switch (step.action) {
    case 'inject':
      return `Administer ${step.hormone || step.title}${dose ? ` at ${dose}` : ''}${route}.`;
    case 'insert':
      return `Insert ${step.hormone || 'progesterone device'}${dose ? ` (${dose})` : ''}${route ? `${route}.` : '.'}`;
    case 'remove':
      return `Remove progesterone device${step.hormone ? ` and give ${step.hormone}` : ''}${dose ? ` at ${dose}` : ''}${route}.`;
    case 'observe':
      return step.instructions || `Observe animal and record findings for ${step.title}.`;
    case 'insemination':
      return 'Perform artificial insemination. Record semen straw ID, technician name, and any heat signs observed.';
    default:
      return step.title;
  }
}

export function formatWrittenStepInstruction(step, index) {
  const dateStr = step.scheduledDate
    ? dayjs(step.scheduledDate).format('dddd, DD MMM YYYY')
    : `Day +${step.dayOffset}`;
  const timeStr = step.scheduledTime || '08:00';

  return {
    stepNumber: index + 1,
    dayOffset: step.dayOffset,
    heading: `Step ${index + 1} — Day +${step.dayOffset} · ${dateStr} · ${timeStr}`,
    title: step.title,
    actionLine: formatSyncStepActionLine(step),
    products: step.productExamples || null,
    instructions: step.instructions || null,
    notes: step.notes || null,
    status: step.status || 'pending',
    isInsemination: step.action === 'insemination',
  };
}

export function getWrittenSyncInstructions(state) {
  const parsed = parseSyncProtocolData(state);
  if (!parsed.steps?.length) return [];
  return parsed.steps.map((step, index) => formatWrittenStepInstruction(step, index));
}

function buildQueueItem(breeding, step, stepIndex, bucket) {
  const parsed = parseSyncProtocolData(breeding.sync_protocol_data);
  const due = dayjs(step.scheduledDate).startOf('day');
  const today = dayjs().startOf('day');
  const total = parsed.steps?.length || 0;
  const done = countCompletedSteps(parsed);

  return {
    id: `${breeding.id}:${step.key || stepIndex}`,
    bucket,
    breedingId: breeding.id,
    animalId: breeding.animal_id || breeding.animal?.id,
    animalLabel: getAnimalLabelFromBreeding(breeding),
    protocolName: breeding.sync_protocol_name || parsed.presetName || 'Sync protocol',
    stepKey: step.key,
    stepTitle: step.title || `Step ${stepIndex + 1}`,
    scheduledDate: step.scheduledDate,
    scheduledTime: step.scheduledTime || '08:00',
    overdue: due.isBefore(today, 'day'),
    progressDone: done,
    progressTotal: total,
    status: breeding.status,
  };
}

/** Build actionable sync work queue from active synchronized breedings. */
export function buildSyncWorkQueue(activeBreedings = []) {
  const today = dayjs().startOf('day');
  const weekEnd = dayjs().endOf('week').startOf('day');
  const dueToday = [];
  const dueThisWeek = [];
  const incomplete = [];
  const seenIncomplete = new Set();

  activeBreedings.forEach((breeding) => {
    const parsed = parseSyncProtocolData(breeding.sync_protocol_data);
    const steps = parsed.steps || [];
    const total = steps.length;
    const done = countCompletedSteps(parsed);
    const animalId = breeding.animal_id || breeding.animal?.id;

    if (total > 0 && done < total && animalId && !seenIncomplete.has(breeding.id)) {
      seenIncomplete.add(breeding.id);
      incomplete.push({
        id: `incomplete:${breeding.id}`,
        bucket: 'incomplete',
        breedingId: breeding.id,
        animalId,
        animalLabel: getAnimalLabelFromBreeding(breeding),
        protocolName: breeding.sync_protocol_name || parsed.presetName || 'Sync protocol',
        stepTitle: `${done}/${total} steps done`,
        scheduledDate: null,
        scheduledTime: null,
        overdue: false,
        progressDone: done,
        progressTotal: total,
        status: breeding.status,
      });
    }

    steps.forEach((step, index) => {
      if ((step.status || 'pending') === 'done' || !step.scheduledDate) return;
      const due = dayjs(step.scheduledDate).startOf('day');

      if (due.isSame(today, 'day') || due.isBefore(today, 'day')) {
        dueToday.push(buildQueueItem(breeding, step, index, 'today'));
        return;
      }

      if (due.isAfter(today, 'day') && !due.isAfter(weekEnd, 'day')) {
        dueThisWeek.push(buildQueueItem(breeding, step, index, 'week'));
      }
    });
  });

  const byDueDate = (a, b) => dayjs(a.scheduledDate).valueOf() - dayjs(b.scheduledDate).valueOf();

  dueToday.sort((a, b) => {
    if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
    return byDueDate(a, b);
  });
  dueThisWeek.sort(byDueDate);
  incomplete.sort((a, b) => {
    const aPct = a.progressTotal ? a.progressDone / a.progressTotal : 0;
    const bPct = b.progressTotal ? b.progressDone / b.progressTotal : 0;
    return aPct - bPct;
  });

  return {
    dueToday,
    dueThisWeek,
    incomplete,
    counts: {
      dueToday: dueToday.length,
      dueThisWeek: dueThisWeek.length,
      incomplete: incomplete.length,
    },
  };
}
